import React from 'react';
import { Settings, ChevronRight, Layers } from 'lucide-react';
import { NavLink } from 'react-router-dom';

interface ContextProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Context: React.FC<ContextProps> = ({ addNotification }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3">
          <Settings className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Test Context Configuration</h1>
            <p className="text-slate-300 mt-1">Manage hierarchical test structure (Domain → Category → Technique) and evaluation configuration</p>
          </div>
        </div>
      </div>

      {/* Hierarchical Structure Info */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Layers className="h-5 w-5 text-indigo-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-indigo-900">New Hierarchical Structure</h4>
            <p className="text-sm text-indigo-700 mt-1">
              Tests are now organized as: <span className="font-mono bg-slate-700 px-2 py-0.5 rounded">Domain → Category → Technique</span>
            </p>
            <p className="text-xs text-indigo-600 mt-1">
              Example: Knowledge Base › Basic Knowledge Test › Positive Page Retrieval
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* Domains Card - NEW */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Layers className="h-6 w-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-100">Domains</h3>
          </div>
          <p className="text-slate-300 mb-4">
            Top-level organization: Knowledge Base, RAG Pipeline, Agent System (3 domains).
          </p>
          <NavLink
            to="/context/domains"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium group"
          >
            Manage Domains
            <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </NavLink>
        </div>

        {/* Categories Card (formerly Test Types) */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-100">Categories</h3>
          </div>
          <p className="text-slate-300 mb-4">
            Test categories within domains (10 categories). Formerly called "Test Types".
          </p>
          <NavLink
            to="/context/test-types"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium group"
          >
            Manage Categories
            <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </NavLink>
        </div>

        {/* Techniques Card (formerly Test Techniques) */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Settings className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-100">Techniques</h3>
          </div>
          <p className="text-slate-300 mb-4">
            Test techniques within categories (13 techniques). Formerly called "Test Techniques".
          </p>
          <NavLink
            to="/context/test-techniques"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium group"
          >
            Manage Techniques
            <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </NavLink>
        </div>

        {/* Evaluation Mappings Card */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Settings className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-100">Evaluation Mappings</h3>
          </div>
          <p className="text-slate-300 mb-4">
            Map techniques to evaluation metrics by framework for optimized evaluation.
          </p>
          <NavLink
            to="/context/evaluation-mappings"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium group"
          >
            Configure Mappings
            <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </NavLink>
        </div>

        {/* Evaluation Frameworks Card */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Layers className="h-6 w-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-100">Evaluation Frameworks</h3>
          </div>
          <p className="text-slate-300 mb-4">
            Manage evaluation frameworks (RAGAS, OpenAI, etc.) and their available metrics.
          </p>
          <NavLink
            to="/context/evaluation-frameworks"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium group"
          >
            Manage Frameworks
            <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </NavLink>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Configuration Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="text-center p-4 bg-indigo-50 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600">3</div>
            <div className="text-sm text-slate-300">Domains</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">10</div>
            <div className="text-sm text-slate-300">Categories</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">13</div>
            <div className="text-sm text-slate-300">Techniques</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">26</div>
            <div className="text-sm text-slate-300">Evaluation Mappings</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">2</div>
            <div className="text-sm text-slate-300">Frameworks</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Context;
